import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, Pencil, Upload, Archive, RotateCcw, Trash2, BookOpen, GraduationCap, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/constants";
import { RoleBadge, StatusBadge } from "@/components/Badges";
import { FileTypeBadge, downloadForm, FORM_WRITERS } from "@/lib/forms";
import { FormEditorDialog } from "@/components/forms/FormEditorDialog";

const Btn = ({ onClick, icon: Icon, children, testId, dark, danger }) => (
  <button onClick={onClick} data-testid={testId} className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition-colors ${dark ? "bg-lasso-ink text-white hover:bg-lasso-navy" : danger ? "border border-rose-200 text-rose-600 hover:bg-rose-50" : "border border-lasso-line bg-white hover:border-lasso-ink"}`}>
    <Icon className="h-4 w-4" /> {children}
  </button>
);

const Links = ({ title, icon: Icon, items, to, testId }) => (
  <div data-testid={testId}>
    <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-zinc-400">{title}</p>
    {items.length ? items.map((x) => (
      <Link key={x.id} to={`${to}${x.id}`} className="mb-2 flex items-center gap-2 rounded-xl border border-lasso-line bg-white px-3 py-2.5 text-sm hover:border-lasso-ink"><Icon className="h-4 w-4 text-lasso-deep" /> {x.title}</Link>
    )) : <p className="text-sm text-muted-foreground">None linked.</p>}
  </div>
);

export default function FormDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [meta, setMeta] = useState({ categories: [] });
  const [preview, setPreview] = useState(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => api.get(`/forms/${id}`).then((r) => setForm(r.data)).catch((e) => toast.error(formatApiError(e))), [id]);
  useEffect(() => { load(); api.get("/forms/meta").then((r) => setMeta(r.data)); }, [load]);
  useEffect(() => {
    if (form?.file_type !== "pdf") return;
    let url;
    api.get(`/forms/${id}/file`, { responseType: "blob" }).then((r) => { url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })); setPreview(url); });
    return () => url && URL.revokeObjectURL(url);
  }, [id, form?.file_id, form?.file_type]);

  const act = async (fn, msg) => {
    try {
      await fn();
      if (msg) toast.success(msg);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const replace = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    await act(() => api.post(`/forms/${id}/replace`, fd), "File replaced — version bumped");
    setBusy(false);
    fileRef.current.value = "";
  };
  const remove = async () => {
    if (!window.confirm(`Permanently delete “${form.title}”? This can't be undone.`)) return;
    try {
      await api.delete(`/forms/${id}`);
      toast.success("Form deleted");
      navigate("/forms");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!form) return null;
  const writer = FORM_WRITERS.includes(user.role);
  return (
    <div className="fade-up">
      <Link to="/forms" className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-zinc-500 hover:text-lasso-ink" data-testid="form-back-link"><ArrowLeft className="h-4 w-4" /> Forms</Link>
      <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
        <div className="max-w-2xl">
          <div className="flex flex-wrap items-center gap-2"><FileTypeBadge type={form.file_type} /><StatusBadge status={form.status} testId="form-status" /><span className="text-xs font-semibold uppercase tracking-[0.14em] text-zinc-400">{form.category}</span></div>
          <h1 className="mt-3 font-display text-3xl font-bold sm:text-4xl" data-testid="form-title">{form.title}</h1>
          <p className="mt-3 text-zinc-600">{form.description}</p>
          <p className="mt-3 text-sm text-zinc-500" data-testid="form-version">Version {form.version} · Last updated {formatDate(form.updated_at)}{form.updated_by ? ` by ${form.updated_by}` : ""}{writer && <span data-testid="form-download-count"> · {form.download_count} downloads · <span data-testid="form-times-used">used {form.times_used ?? 0} times</span></span>}</p>
          <div className="mt-3 flex flex-wrap gap-1.5">{form.applicable_roles.map((r) => <RoleBadge key={r} role={r} />)}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Btn onClick={() => downloadForm(form).then(load).catch((e) => toast.error(formatApiError(e)))} icon={Download} dark testId="form-download-button">Download</Btn>
          {form.can_edit && (
            <>
              <Btn onClick={() => setEditing(true)} icon={Pencil} testId="form-edit-button">Edit</Btn>
              <Btn onClick={() => fileRef.current.click()} icon={busy ? Loader2 : Upload} testId="form-replace-button">Replace file</Btn>
              <input ref={fileRef} type="file" accept=".pdf,.docx,.xlsx" className="hidden" onChange={(e) => replace(e.target.files[0])} data-testid="form-replace-input" />
              {form.status === "published"
                ? <Btn onClick={() => act(() => api.post(`/forms/${id}/status`, { status: "archived" }), "Form archived")} icon={Archive} testId="form-archive-button">Archive</Btn>
                : <Btn onClick={() => act(() => api.post(`/forms/${id}/status`, { status: "published" }), "Form republished")} icon={RotateCcw} testId="form-publish-button">Republish</Btn>}
            </>
          )}
          {user.role === "admin" && <Btn onClick={remove} icon={Trash2} danger testId="form-delete-button">Delete</Btn>}
        </div>
      </div>
      <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_300px]">
        <div className="overflow-hidden rounded-2xl border border-lasso-line bg-white">
          {form.file_type === "pdf" ? (
            preview ? <iframe title="Form preview" src={preview} className="h-[75vh] w-full" data-testid="form-pdf-preview" /> : <p className="p-10 text-sm text-muted-foreground">Loading preview…</p>
          ) : (
            <div className="p-10 text-center" data-testid="form-no-preview">
              <FileTypeBadge type={form.file_type} />
              <p className="mt-4 text-sm text-zinc-600">{form.file_type.toUpperCase()} files can't be previewed in the browser — download to open in {form.file_type === "docx" ? "Word or Google Docs" : "Excel or Google Sheets"}.</p>
            </div>
          )}
        </div>
        <aside className="space-y-8">
          <Links title="Linked SOPs" icon={BookOpen} items={form.linked_sops} to="/sops/" testId="form-linked-sops" />
          <Links title="Linked courses" icon={GraduationCap} items={form.linked_courses} to="/courses/" testId="form-linked-courses" />
          {writer && form.history?.length > 0 && (
            <div data-testid="form-version-history">
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-zinc-400">Previous versions</p>
              {form.history.slice().reverse().map((h) => <p key={h.version} className="text-sm text-zinc-600">v{h.version} · {h.file_name} · replaced {formatDate(h.replaced_at)} by {h.replaced_by}</p>)}
            </div>
          )}
        </aside>
      </div>
      <FormEditorDialog open={editing} form={form} categories={meta.categories} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} />
    </div>
  );
}
